import { memo, useId, useRef, useEffect, useMemo } from 'react';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsSwiper';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import BasicBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/BasicBtn';
import CouponItem2 from '@wconcept/wconcept-ui-renewal/components/molecules/_item/CouponItem2';
import EvtRltdBrandItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/EvtRltdBrandItem';
import EvtVisualBannerItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/EvtVisualBannerItem';
import Prd2LineScrollBar from '@wconcept/wconcept-ui-renewal/components/atoms/Prd2LineScrollBar';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import { IconArrowRight09 } from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { Swiper as SwiperClass } from 'swiper/types';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  brandCd?: string;
  colorType?: ColorType;
  brandName: string;
  brandNameEn?: string;
  keyVisualImgSrc?: string;
  keyVisualTitle?: string;
  keyVisualSubTitle?: string;
  couponTitle?: string;
  couponDesc?: string;
  productList?: React.ReactNode;
  isDesktop?: boolean;
  keyVisualImgRs?: string;
  onClickMore?: () => void;
  btnText?: string;
  brandMainRef?: React.RefObject<SwiperClass | null>;
  onClickCoupon?: () => void;
  onClickBrandBanner?: () => void;
  status?: '1' | '2' | '3';
}

const BrandMainSection2 = ({
  brandCd,
  colorType = 'white',
  brandName,
  brandNameEn,
  keyVisualImgSrc,
  keyVisualTitle,
  keyVisualSubTitle,
  couponTitle,
  couponDesc,
  productList,
  isDesktop,
  keyVisualImgRs,
  onClickMore,
  btnText = '자세히 보기',
  onClickCoupon,
  onClickBrandBanner,
  brandMainRef,
  status,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const id = useId();
  const safeId = id.replace(/:/g, '');
  const uniqueKeyProductList = useMemo(
    () => `product-list-swiper-${safeId}`,
    [safeId]
  );
  const swiperRef = useRef<SwiperClass>(null);

  useEffect(() => {
    if (swiperRef.current && isDesktop) {
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.update();
          if (swiperRef.current.navigation) {
            swiperRef.current.navigation.destroy();
            swiperRef.current.navigation.init();
            swiperRef.current.navigation.update();
          }
        }
      }, 0);
    }
  }, [brandCd, isDesktop, productList]);

  const derivedBgType = isCustomColor ? 'black' : colorType;

  const couponColorType = (() => {
    if (isCustomColor) return customColors;
    if (derivedBgType === 'white') return 'black';
    return 'gray';
  })();

  const couponItemProps = {
    title: couponTitle,
    desc: couponDesc,
    status: status === '3' ? 'comingsoon' : 'download',
    isDesktop: true,
    colorType: couponColorType,
    onClick: onClickCoupon,
  };

  const brandItemProps = {
    brandName,
    brandNameEn,
    type: 'square2',
    bgType: isCustomColor ? 'white' : colorType,
    onClick: onClickBrandBanner,
  };

  const visualBannerProps = {
    imgSrc: keyVisualImgSrc,
    rs: keyVisualImgRs,
    title: keyVisualTitle,
    subTitle: keyVisualSubTitle,
  };

  const cName = classNameBind([
    'event-brand-main',
    !isCustomColor ? `bg-type-${colorType}` : '',
  ]);

  const renderMoreButton = () => (
    <BasicBtn className="btn-more" text={btnText} onClick={onClickMore} />
  );

  const renderDesktopContent = () => {
    const productParamPc: any = {
      autoplay: false,
      loop: false,
      slidesPerView: 6,
      slidesPerGroup: 6,
      spaceBetween: 17.5,
      navigation: {
        prevEl: `.${uniqueKeyProductList} .swiper-button-prev`,
        nextEl: `.${uniqueKeyProductList} .swiper-button-next`,
      },
      navigationEl: (
        <SwiperNavigation className={uniqueKeyProductList} offsetTop={110} />
      ),
    };

    return (
      <div className="brand-container">
        <div className="brand-grid">
          <div className="brand-banner">
            <EvtVisualBannerItem {...visualBannerProps} />
          </div>
          <div className="brand-info">
            <EvtRltdBrandItem {...brandItemProps} />
            {couponTitle && <CouponItem2 {...couponItemProps} />}
          </div>
        </div>
        <div className="brand-product-list">
          {productList && (
            <ItemsSwiper
              {...productParamPc}
              className="product-list"
              onSwiper={(swiper: SwiperClass) => {
                swiperRef.current = swiper;
                if (brandMainRef && 'current' in brandMainRef) {
                  // eslint-disable-next-line no-param-reassign
                  brandMainRef.current = swiper;
                }
              }}
            >
              {productList}
            </ItemsSwiper>
          )}
          {renderMoreButton()}
        </div>
      </div>
    );
  };

  const renderMobileContent = () => (
    <>
      <div className="brand-container">
        {keyVisualImgSrc && (
          <div className="brand-banner">
            <EvtVisualBannerItem {...visualBannerProps} />
            <Button className="btn-more-small" onClick={onClickMore}>
              자세히 보기
              <IconArrowRight09 />
            </Button>
          </div>
        )}
        {couponTitle && <CouponItem2 {...couponItemProps} />}
        <div className="brand-product-list">
          <Prd2LineScrollBar perView={3.14} rowGap={22} colGap={4}>
            {productList}
          </Prd2LineScrollBar>

          {/* 배너가 없으면 배너 위 자세히보기(.btn-more-small)를 붙일 곳이 없으므로
              다른 모듈처럼 상품 리스트 하단에 노출한다.
              단 미도래 탭은 MO에서 자세히보기를 노출하지 않는 정책이라 제외한다. */}
          {!keyVisualImgSrc && status !== '3' && renderMoreButton()}
        </div>
      </div>
    </>
  );

  return (
    <S.BrandMainSection2
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
    >
      {isDesktop ? renderDesktopContent() : renderMobileContent()}
    </S.BrandMainSection2>
  );
};

export default memo(BrandMainSection2);
